import React from 'react';
import DayTabs from './DayTabs';
import DTEControls from './DTEControls';
import ScopeGateBanner from './ScopeGateBanner';
import { useDteOverlay } from './useDteOverlay';
import { Card, CardContent } from '../ui/Card';
import PositionTableView from '../positions/PositionTableView';
import ConfirmTerminateDialog from '../positions/ConfirmTerminateDialog';
import { scheduleToPositionRow } from '../../utils/scheduleToPositionRow';
import { Info } from 'lucide-react';
import ProHeroBanner from './pro/ProHeroBanner';
import SharedToolbar from './SharedToolbar';
import AnomalyBanner from './AnomalyBanner';
import { computePositionsTotals } from '../../utils/positionsScheduleTotals';
import { computeAnomalies } from '../../utils/scheduleAnomalies';
import { POSITION_STATUS, ENTRY_STATUS } from '../../constants/tradingConstants';
import { toPositionsCsv, positionsCsvFilename } from '../../utils/positionsCsv';
import { downloadFile } from '../../utils/downloadFile';
import type { ScheduleDataBundle } from './useScheduleData';
import type { PositionRow } from '../../types';
import type { ScheduleRow } from '../../types/schedule';

interface PositionsScheduleViewProps {
  data: ScheduleDataBundle;
}

/**
 * Positions view — third tab in the unified Schedule hub (Phase B3).
 * Renders the multi-select PositionTableView via the existing
 * scheduleToPositionRow adapter, sourced from the new `format=positions`
 * flat-list endpoint (Phase B2) plumbed through useScheduleData.
 *
 * Page-level Search (owned by SchedulePage) feeds this view's filter
 * predicate; per-row actions (Exit / Cancel / Retry / multi-select bulk
 * exit) dispatch to handlers from useScheduleData. The standalone
 * /positions page coexists for ≥1 trading week (Phase C validation gate);
 * both surfaces share PositionTableView so any fix lands on both.
 */
const PositionsScheduleView: React.FC<PositionsScheduleViewProps> = ({ data }) => {
  const {
    selectedDate, setSelectedDate,
    positions,
    isToday,
    isBrokerScope, canAct,
    pageSearchQuery, setPageSearchQuery,
    bulkActionPending,
    retryingId, exitingId, terminatingId,
    retryPosition, exitPosition, exitPositionsBulk,
    exitAllBulk,
    askTerminatePosition, askTerminateAll,
    terminateRequest, terminateScopeLabel, closeTerminate, onTerminateConfirm,
    currentIstHms,
    confirmDialog,
  } = data;

  const dte = useDteOverlay();

  // Apply the page-level search predicate to the RAW ScheduleRow list first
  // so the same predicate the Classic/Pro views use (strategy_name +
  // basket_name + symbol + client_id substring) drives both the KPI banner
  // totals AND the table — without that, hero counts and table rows would
  // diverge after a search.
  const filteredScheduleRows: ScheduleRow[] = React.useMemo(() => {
    const q = pageSearchQuery.trim().toLowerCase();
    if (!q) return positions;
    return positions.filter((p) =>
      (p.strategy_name?.toLowerCase().includes(q) ?? false)
      || (p.basket_name?.toLowerCase().includes(q) ?? false)
      || (p.symbol?.toLowerCase().includes(q) ?? false)
      || (p.client_id?.toLowerCase().includes(q) ?? false),
    );
  }, [positions, pageSearchQuery]);

  // Adapt the filtered list to PositionRow for the table.
  const filtered: PositionRow[] = React.useMemo(
    () => filteredScheduleRows.map(scheduleToPositionRow),
    [filteredScheduleRows],
  );

  // Derive KPI totals from the filtered positions so the hero banner reflects
  // exactly what's in the table beneath it.
  const filteredTotals = React.useMemo(
    () => computePositionsTotals(filteredScheduleRows),
    [filteredScheduleRows],
  );

  // Exit All eligibility: count rows the bulk handler would actually act on
  // (live OPEN / EXIT_PENDING + pending ENTRY orders not already rejected).
  const exitableCount = React.useMemo(
    () => filteredScheduleRows.reduce((n, p) => {
      const live = p.position_status === POSITION_STATUS.OPEN
        || p.position_status === POSITION_STATUS.EXIT_PENDING;
      const pendingEntry = p.position_status === POSITION_STATUS.ENTRY_PENDING
        && p.entry_status !== ENTRY_STATUS.REJECTED;
      return n + (live || pendingEntry ? 1 : 0);
    }, 0),
    [filteredScheduleRows],
  );
  const countLabel = `${filtered.length} ${
    filtered.length === 1 ? 'Position' : 'Positions'
  }`;

  // CSV export — pure call against the already-filtered ScheduleRow slice
  // (Search + selected date applied). Filename embeds the schedule date
  // plus current HHmmss so multiple downloads in a session don't clobber
  // each other. Available on any date (today or historical) since exports
  // are read-only.
  const handleExportCsv = React.useCallback(() => {
    const csv = toPositionsCsv(filteredScheduleRows);
    downloadFile(positionsCsvFilename(selectedDate), csv, 'text/csv;charset=utf-8');
  }, [filteredScheduleRows, selectedDate]);

  // Wire row-action handlers — page-level handlers take (positionId), the
  // table passes (PositionRow); thin adapter callbacks bridge that. Gate
  // on canAct: historical dates and Platform scope are view-only.
  const onExit = canAct
    ? (p: PositionRow) => exitPosition(p.positionId)
    : undefined;
  const onRetry = canAct
    ? (p: PositionRow) => retryPosition(p.positionId)
    : undefined;
  const onTerminate = canAct
    ? (p: PositionRow) => askTerminatePosition(p.positionId)
    : undefined;
  const onExitMultiple = canAct ? exitPositionsBulk : undefined;

  return (
    <div className="space-y-4">
      {/* Date strip — Classic/Pro have the same controls; keep them here
          so the Positions tab feels like a sibling view, not an outlier. */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0 space-y-2">
          <DTEControls value={dte.prefs} onChange={dte.setPrefs} />
          <DayTabs
            selectedDate={selectedDate}
            onChange={setSelectedDate}
            dteByDate={dte.dteByDate}
            holidays={dte.holidays}
            onDatesChange={dte.onDatesChange}
          />
        </div>
      </div>

      {/* KPI banner — same ProHeroBanner used by Classic/Pro, fed from a
          positions-shaped totals computation so day-level P&L + counts
          stay visible when the user moves to the Positions tab. */}
      {positions.length > 0 && (
        <ProHeroBanner
          totals={filteredTotals}
          selectedDate={selectedDate}
          isToday={isToday}
          dayHasActivity={positions.length > 0}
        />
      )}

      {/* Anomaly banner — informational only on the Positions tab.
          PositionTableView owns its own filter layer and doesn't expose
          a column-filter setter, so chips render as badges (no click).
          The schedule status counts (rejected / cancelled / sl_pending)
          come from the positions-shaped totals helper. */}
      <AnomalyBanner
        anomalies={computeAnomalies(filteredTotals, { currentIstHms, isToday })}
      />

      <ScopeGateBanner isToday={isToday} isBrokerScope={isBrokerScope} />

      {/* Shared toolbar — same component the other two views render, just
          below the hero so the toolbar reads as a table-scope row of
          controls. */}
      <SharedToolbar
        countLabel={countLabel}
        searchQuery={pageSearchQuery}
        onSearchChange={setPageSearchQuery}
        currentIstHms={currentIstHms}
        onExitAll={canAct ? exitAllBulk : undefined}
        exitableCount={exitableCount}
        exitAllPending={bulkActionPending}
        onTerminateAll={canAct ? askTerminateAll : undefined}
        isToday={isToday}
        onExportCsv={handleExportCsv}
        exportRowCount={filteredScheduleRows.length}
      />

      <Card>
        <CardContent className="p-0">
          {!isToday && (
            <div className="px-6 py-2 border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-800 dark:text-amber-200 flex items-center gap-2">
              <Info className="w-3.5 h-3.5 shrink-0" />
              <span>
                Viewing historical data for{' '}
                <span className="font-semibold tabular-nums">{selectedDate}</span>{' '}
                — actions (Exit, Cancel, Retry) are disabled. Switch to today to manage live positions.
              </span>
            </div>
          )}
          <div className="overflow-x-auto">
            <PositionTableView
              positions={filtered}
              onExit={onExit}
              onExitMultiple={onExitMultiple}
              onRetry={onRetry}
              onTerminate={onTerminate}
              exitingId={exitingId}
              retryingId={retryingId}
              terminatingId={terminatingId}
              isToday={isToday}
            />
          </div>
        </CardContent>
      </Card>
      {confirmDialog}
      <ConfirmTerminateDialog
        isOpen={!!terminateRequest}
        request={terminateRequest}
        scopeLabel={terminateScopeLabel}
        onClose={closeTerminate}
        onConfirm={onTerminateConfirm}
      />
    </div>
  );
};

export default PositionsScheduleView;
